import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzImageService } from 'ng-zorro-antd/image';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { date_format, datetime_format } from 'src/date/date.configuration';
import { TableRow } from 'src/app/common/table-row.interface';
import { ReusableTableComponent } from 'src/app/common/reusable-table/reusable-table.component';
import { IDischargeReportPreviewData } from 'src/app/interfaces/DischargeReport/IDischargeReportPreview';
import { IDischargeReportIssueRequest } from 'src/app/interfaces/DischargeReport/IDischargeReportIssue';
import {
  IDischargeReportErrorResponse,
  IDischargeReportValidationIssue,
} from 'src/app/interfaces/DischargeReport/IDischargeReportValidation';
import {
  IEHDSAttachment,
  IEHDSCodeableConcept,
  IEHDSHumanName,
  IEHDSPeriod,
} from 'src/app/interfaces/DischargeReport/IEHDSCommon';
import {
  IEHDSCarePlanActivity,
  IEHDSCondition,
  IEHDSDeviceUse,
  IEHDSMedicalAlert,
  IEHDSMedicationUse,
  IEHDSProcedure,
} from 'src/app/interfaces/DischargeReport/IEHDSClinical';
import {
  IDischargeReportPreviewError,
  DischargeReportService,
} from 'src/app/services/dischargeReport.service';
import { SwalToastService } from 'src/app/services/swal.service';
import { DischargeReportHeaderComponent } from './discharge-report-header/discharge-report-header.component';
import { DischargeReportValidationSummaryComponent } from './discharge-report-validation-summary/discharge-report-validation-summary.component';
import { DischargeReportSectionComponent } from './discharge-report-section/discharge-report-section.component';

interface INarrativeParts {
  label?: string;
  rest: string;
}

const ISO_DATETIME_PATTERN = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z/g;

// "lifecycle" warnings/issues (e.g. PREVIEW_NOT_LEGALLY_FINAL, REPORT_ALREADY_ISSUED)
// describe issuance state. They're shown normally like any other warning, but once
// the report is actually issued (issuedReport.exists), they're stale/already covered
// by the header banner + Download PDF button, so they're dropped only at that point.
const VALIDATION_CATEGORIES_STALE_ONCE_ISSUED = ['lifecycle'];

@Component({
  selector: 'app-discharge-report-preview',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    NzLayoutModule,
    NzIconModule,
    NzSpinModule,
    NzButtonModule,
    NzCheckboxModule,
    NzModalModule,
    PdfViewerModule,
    ReusableTableComponent,
    DischargeReportHeaderComponent,
    DischargeReportValidationSummaryComponent,
    DischargeReportSectionComponent,
  ],
  templateUrl: './discharge-report-preview.component.html',
  styleUrls: ['./discharge-report-preview.component.css'],
})
export class DischargeReportPreviewComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  isLoading = false;
  isDownloadingPdf = false;
  data?: IDischargeReportPreviewData;
  error?: IDischargeReportPreviewError;

  showIssueConfirmation = false;
  isIssuing = false;
  issueForm: FormGroup = this.fb.group({
    attestationConfirmed: [false, Validators.requiredTrue],
    legalAuthenticationConfirmed: [false, Validators.requiredTrue],
  });
  private issueIdempotencyKey = '';

  attachmentPreviewUrl?: string;
  isPdfPreviewVisible = false;
  pdfPreviewFileName?: string;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dischargeReportService: DischargeReportService,
    private readonly swalToastService: SwalToastService,
    private readonly imageService: NzImageService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.dischargeReportService.isLoadingSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoading) => (this.isLoading = isLoading));

    this.dischargeReportService.dataSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => (this.data = data));

    this.dischargeReportService.errorSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((error) => (this.error = error));

    this.dischargeReportService.isDownloadingPdfSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDownloadingPdf) => (this.isDownloadingPdf = isDownloadingPdf));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.revokeAttachmentPreviewUrl();
  }

  handleCancel(): void {
    this.dischargeReportService.close();
  }

  get canDownloadPdf(): boolean {
    return !!this.data?.issuedReport?.exists;
  }

  get validationIssues(): IDischargeReportValidationIssue[] {
    return this.dropStaleLifecycleEntries(this.data?.validation?.issues);
  }

  get validationWarnings(): IDischargeReportValidationIssue[] {
    return this.dropStaleLifecycleEntries(this.data?.validation?.warnings);
  }

  private dropStaleLifecycleEntries(
    entries?: IDischargeReportValidationIssue[]
  ): IDischargeReportValidationIssue[] {
    if (!this.data?.issuedReport?.exists) {
      return entries ?? [];
    }

    return (entries ?? []).filter(
      (entry) => !VALIDATION_CATEGORIES_STALE_ONCE_ISSUED.includes(entry.category)
    );
  }

  handleDownloadPdf(): void {
    const reportId = this.data?.issuedReport?.exists
      ? this.data.issuedReport.reportId
      : undefined;
    if (!reportId) {
      return;
    }

    this.dischargeReportService.downloadPdf(reportId);
  }

  get canIssue(): boolean {
    return !!this.data?.validation?.canIssue;
  }

  openIssueConfirmation(): void {
    this.issueForm.reset({
      attestationConfirmed: false,
      legalAuthenticationConfirmed: false,
    });
    this.issueIdempotencyKey = crypto.randomUUID();
    this.showIssueConfirmation = true;
  }

  cancelIssueConfirmation(): void {
    this.showIssueConfirmation = false;
  }

  confirmIssue(): void {
    if (!this.data?.preview || this.issueForm.invalid || this.isIssuing) {
      return;
    }

    const request: IDischargeReportIssueRequest = {
      previewToken: this.data.preview.previewToken,
      expectedJsonSha256: this.data.preview.jsonSha256,
      attestationConfirmed: this.issueForm.value.attestationConfirmed,
      legalAuthenticationConfirmed:
        this.issueForm.value.legalAuthenticationConfirmed,
    };

    this.isIssuing = true;

    this.dischargeReportService
      .issue(request, this.issueIdempotencyKey)
      .subscribe({
        next: (response) => {
          this.isIssuing = false;
          this.showIssueConfirmation = false;
          this.swalToastService.toastSuccess(
            'Discharge report issued successfully'
          );
          this.dischargeReportService.applyIssuedResult(response.data!);
        },
        error: (err: HttpErrorResponse) => {
          this.isIssuing = false;
          const body = err?.error as IDischargeReportErrorResponse | null;
          this.swalToastService.toastError(
            body?.message || 'Unable to issue the discharge report'
          );
          console.error('Error issuing discharge report:', err);
        },
      });
  }

  get alertRows(): TableRow[] {
    return this.mapMedicalAlerts(this.data?.report?.body?.alerts?.medicalAlert);
  }

  get conditionRows(): TableRow[] {
    return this.mapConditions(
      this.data?.report?.body?.courseOfEncounter?.diagnosticSummary
    );
  }

  get procedureRows(): TableRow[] {
    return this.mapProcedures(
      this.data?.report?.body?.courseOfEncounter?.procedures
    );
  }

  get deviceRows(): TableRow[] {
    return this.mapDevices(
      this.data?.report?.body?.courseOfEncounter?.medicalDevicesAndImplants
    );
  }

  get pastProblemRows(): TableRow[] {
    return this.mapPastProblems(
      this.data?.report?.body?.patientHistory?.pastProblems
    );
  }

  get pastProcedureRows(): TableRow[] {
    return this.mapProcedures(
      this.data?.report?.body?.patientHistory?.procedures
    );
  }

  get pastDeviceRows(): TableRow[] {
    return this.mapDevices(
      this.data?.report?.body?.patientHistory?.devicesAndImplants
    );
  }

  get pharmacotherapyRows(): TableRow[] {
    return this.mapMedicationUses(
      this.data?.report?.body?.courseOfEncounter?.pharmacotherapy
    );
  }

  get medicationSummaryRows(): TableRow[] {
    return this.mapMedicationUses(
      this.data?.report?.body?.medicationSummary?.medicationUse
    );
  }

  get carePlanRows(): TableRow[] {
    return this.mapCarePlanActivities(
      this.data?.report?.body?.carePlan?.carePlan
    );
  }

  get admitterName(): string | undefined {
    return this.humanNameText(
      this.data?.report?.body?.encounterInformation?.encounter?.admission
        ?.admitter?.name
    );
  }

  get attachments(): IEHDSAttachment[] {
    return this.data?.report?.attachments ?? [];
  }

  attachmentIcon(attachment: IEHDSAttachment): string {
    const contentType = attachment.contentType?.coding?.[0]?.code ?? '';
    if (contentType.includes('pdf')) {
      return 'file-pdf';
    }
    if (contentType.startsWith('image/')) {
      return 'file-image';
    }
    return 'file';
  }

  attachmentSizeText(attachment: IEHDSAttachment): string | undefined {
    return attachment.size ? this.formatAttachmentSize(attachment.size) : undefined;
  }

  attachmentTypeText(attachment: IEHDSAttachment): string | undefined {
    return this.codeableConceptText(attachment.contentType);
  }

  canPreviewAttachment(attachment: IEHDSAttachment): boolean {
    if (!attachment.url) {
      return false;
    }

    const contentType = attachment.contentType?.coding?.[0]?.code?.toLowerCase() ?? '';
    const title = (attachment.title ?? '').toLowerCase();

    return (
      contentType.includes('pdf') ||
      contentType.startsWith('image/') ||
      /\.(pdf|png|jpe?g|webp|gif|bmp|svg)$/i.test(title)
    );
  }

  viewAttachment(attachment: IEHDSAttachment): void {
    if (!attachment.url || !this.canPreviewAttachment(attachment)) {
      return;
    }

    this.dischargeReportService.downloadAttachment(attachment.url).subscribe({
      next: (response: HttpResponse<Blob>) => {
        const contentType =
          response.headers.get('Content-Type') ||
          attachment.contentType?.coding?.[0]?.code ||
          'application/octet-stream';
        const objectUrl = URL.createObjectURL(
          new Blob([response.body!], { type: contentType })
        );

        if (contentType.startsWith('image/')) {
          const previewRef = this.imageService.preview([
            { src: objectUrl, alt: attachment.title },
          ]);
          const originalClose = previewRef.close.bind(previewRef);
          previewRef.close = () => {
            originalClose();
            URL.revokeObjectURL(objectUrl);
          };
          return;
        }

        this.revokeAttachmentPreviewUrl();
        this.attachmentPreviewUrl = objectUrl;
        this.pdfPreviewFileName = attachment.title;
        this.isPdfPreviewVisible = true;
      },
      error: () => {
        this.swalToastService.toastError('Unable to retrieve the attachment');
      },
    });
  }

  closePdfPreview(): void {
    this.isPdfPreviewVisible = false;
    this.revokeAttachmentPreviewUrl();
  }

  private revokeAttachmentPreviewUrl(): void {
    if (this.attachmentPreviewUrl) {
      URL.revokeObjectURL(this.attachmentPreviewUrl);
      this.attachmentPreviewUrl = undefined;
    }
  }

  splitNarrative(narrative?: string): INarrativeParts | undefined {
    if (!narrative) {
      return undefined;
    }

    const colonIndex = narrative.indexOf(':');
    if (colonIndex <= 0 || colonIndex > 40) {
      return { rest: narrative };
    }

    return {
      label: narrative.slice(0, colonIndex),
      rest: narrative.slice(colonIndex + 1).trim(),
    };
  }

  formatDate(value?: string): string {
    return value ? moment(value).format(date_format) : '';
  }

  formatDateTime(value?: string): string {
    return value ? moment(value).format(datetime_format) : '';
  }

  private mapMedicalAlerts(alerts?: IEHDSMedicalAlert[]): TableRow[] {
    return (alerts ?? []).map((alert, index) => ({
      id: index,
      alert: this.codeableConceptText(alert.description),
      status: this.codeableConceptText(alert.header?.status),
    }));
  }

  private mapConditions(conditions?: IEHDSCondition[]): TableRow[] {
    return (conditions ?? []).map((condition, index) => ({
      id: index,
      diagnosis: this.codeableConceptText(condition.problem),
      status: this.codeableConceptText(condition.diagnosisAssertionStatus),
    }));
  }

  private mapPastProblems(conditions?: IEHDSCondition[]): TableRow[] {
    return (conditions ?? []).map((condition, index) => ({
      id: index,
      diagnosis: this.codeableConceptText(condition.problem),
      status: this.codeableConceptText(condition.diagnosisAssertionStatus),
      resolution: condition.resolutionCircumstances,
    }));
  }

  private mapProcedures(procedures?: IEHDSProcedure[]): TableRow[] {
    return (procedures ?? []).map((procedure, index) => ({
      id: index,
      procedure: this.codeableConceptText(procedure.code),
      note: procedure.note,
    }));
  }

  private mapDevices(devices?: IEHDSDeviceUse[]): TableRow[] {
    return (devices ?? []).map((device, index) => ({
      id: index,
      device:
        this.codeableConceptText(device.deviceCodeableConcept) ??
        device.deviceEHDSDevice?.name?.[0]?.value,
      note: device.note,
    }));
  }

  private mapMedicationUses(medicationUses?: IEHDSMedicationUse[]): TableRow[] {
    return (medicationUses ?? []).map((medicationUse, index) => ({
      id: index,
      medication:
        medicationUse.medication?.productName ??
        this.codeableConceptText(
          medicationUse.medication?.identifyingCodeCodeableConcept?.[0]
        ),
      status: this.codeableConceptText(medicationUse.treatmentStatus),
      change: this.codeableConceptText(medicationUse.changeType?.[0]),
      dosage: medicationUse.dosageInstructions?.renderedDosageInstruction
        ? this.cleanEmbeddedTimestamps(
            medicationUse.dosageInstructions.renderedDosageInstruction
          )
        : undefined,
      onSetDateTime: medicationUse.periodOfUse?.start,
      endDateTime: medicationUse.periodOfUse?.end,
    }));
  }

  private mapCarePlanActivities(
    activities?: IEHDSCarePlanActivity[]
  ): TableRow[] {
    return (activities ?? []).map((activity, index) => ({
      id: index,
      plan: this.withFallback(
        activity.title ??
          this.codeableConceptText(activity.activityCodeableConcept?.[0])
      ),
      description: this.withFallback(activity.description),
      activity: this.withFallback(
        this.codeableConceptListText(activity.activityCodeableConcept)
      ),
      period: this.withFallback(this.formatPeriodRange(activity.period)),
      status: this.withFallback(
        this.codeableConceptText(activity.header?.status)
      ),
    }));
  }

  private withFallback(value?: string): string {
    return value ?? this.translate.instant('Not recorded');
  }

  private formatPeriodRange(period?: IEHDSPeriod): string | undefined {
    const start = period?.start ? this.formatDateTime(period.start) : undefined;
    const end = period?.end ? this.formatDateTime(period.end) : undefined;

    if (start && end) {
      return `${start} - ${end}`;
    }

    return start ?? end;
  }

  private codeableConceptText(
    concept?: IEHDSCodeableConcept
  ): string | undefined {
    if (!concept) {
      return undefined;
    }

    if (concept.text) {
      return concept.text;
    }

    const coding = concept.coding?.[0];
    return coding?.display || coding?.code || undefined;
  }

  private codeableConceptListText(
    concepts?: IEHDSCodeableConcept[]
  ): string | undefined {
    const texts = (concepts ?? [])
      .map((concept) => this.codeableConceptText(concept))
      .filter((text): text is string => !!text);

    return texts.length ? texts.join('; ') : undefined;
  }

  private humanNameText(name?: IEHDSHumanName): string | undefined {
    if (!name) {
      return undefined;
    }

    if (name.text) {
      return name.text;
    }

    const parts = [...(name.given ?? []), ...(name.family ?? [])];
    return parts.length ? parts.join(' ') : undefined;
  }

  private cleanEmbeddedTimestamps(text: string): string {
    return text.replace(ISO_DATETIME_PATTERN, (match) => {
      const parsed = moment(match, moment.ISO_8601, true);
      return parsed.isValid() ? parsed.format(date_format) : match;
    });
  }

  private formatAttachmentSize(sizeInBytes: string): string {
    const bytes = Number(sizeInBytes);
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return sizeInBytes;
    }

    if (bytes < 1024) {
      return `${bytes} B`;
    }

    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    return `${(kb / 1024).toFixed(1)} MB`;
  }
}